import type { MuxClient } from "@mux/sdk/v2/client"
import { errorMessage, HttpError } from "@/transport/http"

/** What the generated client resolves with: `data` on success, `error` and usually `response` on failure. */
export type SdkResult = { data?: unknown; error?: unknown; response?: Response }

type Method = "get" | "post" | "put" | "delete"
type SdkRequestOptions = {
  url: string
  path?: Record<string, string>
  query?: Record<string, string | number>
  body?: unknown
  signal?: AbortSignal
}
type RawClient = Record<Method, (options: SdkRequestOptions & { throwOnError: false }) => Promise<SdkResult>>

/**
 * Sends a request the generated SDK does not describe through a client's
 * configured transport. HTTP and network failures resolve instead of throwing,
 * whatever the client's `throwOnError`, so the caller keeps the status.
 */
export function sdkRequest(client: MuxClient, method: Method, options: SdkRequestOptions): Promise<SdkResult> {
  // MuxClient keeps its configured transport in a protected field.
  const raw = (client as unknown as { client: RawClient }).client
  return raw[method]({ ...options, throwOnError: false })
}

/** The failure a result reports: the server's message, else `fallback`, with the status when a response arrived. */
export function sdkResultError(result: SdkResult, fallback: string): Error {
  const message = errorMessage(result.error, fallback)
  return result.response ? new HttpError(result.response.status, message, result.response) : new Error(message)
}

export class CreateRequestError extends Error {
  /**
   * `outcomeUnknown` means an attempt may have reached the machine, which can
   * already hold what it creates; the error does not prove nothing was created.
   */
  constructor(message: string, readonly status?: number, readonly outcomeUnknown = false) {
    super(message)
    this.name = "CreateRequestError"
  }
}

const CREATE_TIMEOUT_MS = 15_000
const CREATE_ATTEMPTS = 2

/**
 * Posts a request that creates something under IDs the client chose. The
 * machine answers a repeat with what the first attempt created, so a lost,
 * failed or timed-out response is retried once. A 4xx answer is final.
 */
export async function postCreate<T>(
  client: MuxClient,
  request: {
    url: string
    body: unknown
    /** Names the request in its errors, e.g. "Fork". */
    name: string
    decode: (data: unknown) => T
    signal?: AbortSignal
  },
): Promise<T> {
  const failed = `${request.name} failed`
  let lastError: CreateRequestError | undefined
  // Set once an attempt may have created it; a later rejection then answers a retry, not the request.
  let outcomeUnknown = false

  for (let attempt = 0; attempt < CREATE_ATTEMPTS; attempt++) {
    if (request.signal?.aborted) throw new CreateRequestError(`${request.name} canceled`, undefined, outcomeUnknown)
    const controller = new AbortController()
    let timedOut = false
    const cancel = () => controller.abort()
    request.signal?.addEventListener("abort", cancel, { once: true })
    const timeout = setTimeout(() => {
      timedOut = true
      controller.abort()
    }, CREATE_TIMEOUT_MS)

    let rejection: CreateRequestError | undefined
    try {
      const result = await sdkRequest(client, "post", { url: request.url, body: request.body, signal: controller.signal })
      if (result.data !== undefined) return request.decode(result.data)
      const status = result.response?.status
      if (request.signal?.aborted) throw new CreateRequestError(`${request.name} canceled`, undefined, true)
      if (status !== undefined && status >= 400 && status < 500) {
        rejection = new CreateRequestError(errorMessage(result.error, failed), status, outcomeUnknown)
      } else {
        outcomeUnknown = true
        lastError = new CreateRequestError(
          timedOut ? `${request.name} timed out` : errorMessage(result.error, failed),
          status,
          true,
        )
      }
    } catch (error) {
      if (error instanceof CreateRequestError) throw error
      if (request.signal?.aborted) throw new CreateRequestError(`${request.name} canceled`, undefined, true)
      outcomeUnknown = true
      lastError = new CreateRequestError(timedOut ? `${request.name} timed out` : errorMessage(error, failed), undefined, true)
    } finally {
      clearTimeout(timeout)
      request.signal?.removeEventListener("abort", cancel)
    }
    if (rejection) throw rejection
  }

  throw lastError ?? new CreateRequestError(failed)
}
